import { Router } from 'express';
import { UploadFileController } from '../controlles/attchament.controller';
import { authMiddleware } from '../../../middlewares/authMiddleware';
import { UploadMultipleFiles } from '../../../middlewares/storage';
import { Routes } from '../../../interface/routes.interface';

/**
 * @swagger
 * tags:
 *   name: Attachments
 *   description: File upload and management operations
 */
export class AttachmentRouter implements Routes {
    public router = Router();
    public uploadController = new UploadFileController();
    public path = '/upload';

    constructor() {
        this.initializeRoutes();
    }

    public initializeRoutes() {
        /**
         * @swagger
         * /upload:
         *   post:
         *     summary: Upload files
         *     tags: [Attachments]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: moduleName
         *         schema:
         *           type: string
         *           enum: [profileImage, certificate, identification]
         *         required: false
         *         description: The module name for the attachment (e.g., 'identification' for ID documents, 'certificate' for certifications)
         *     requestBody:
         *       required: true
         *       content:
         *         multipart/form-data:
         *           schema:
         *             type: object
         *             properties:
         *               profileImage:
         *                 type: string
         *                 format: binary
         *               identification:
         *                 type: array
         *                 items:
         *                   type: string
         *                   format: binary
         *               videos:
         *                 type: array
         *                 items:
         *                   type: string
         *                   format: binary
         *               certificate:
         *                 type: array
         *                 items:
         *                   type: string
         *                   format: binary
         *     responses:
         *       200:
         *         description: Files uploaded successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                 message:
         *                   type: string
         *                 uploadedFiles:
         *                   type: array
         *                   items:
         *                     $ref: '#/components/schemas/AttachmentResponse'
         *                 failedFiles:
         *                   type: array
         *                   items:
         *                     type: object
         *                     properties:
         *                       filename:
         *                         type: string
         *                       reason:
         *                         type: string
         *       400:
         *         description: Invalid file type, size, or module name
         *       401:
         *         description: Unauthorized - Invalid or missing Bearer token
         */
        this.router.post(
            `${this.path}`,
            UploadMultipleFiles(),
            authMiddleware,
            this.uploadController.uploadData
        );

        /**
         * @swagger
         * /upload:
         *   get:
         *     summary: Get all attachments
         *     tags: [Attachments]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: moduleName
         *         schema:
         *           type: string
         *           enum: [profileImage, certificate, document]
         *         description: Filter attachments by module name
         *     responses:
         *       200:
         *         description: List of attachments
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                 message:
         *                   type: string
         *                 data:
         *                   type: array
         *                   items:
         *                     $ref: '#/components/schemas/AttachmentResponse'
         *       401:
         *         description: Unauthorized - Invalid or missing Bearer token
         *       500:
         *         description: Internal server error
         */
        this.router.get(
            `${this.path}`,
            authMiddleware,
            this.uploadController.getImages
        );

        /**
         * @swagger
         * /upload:
         *   delete:
         *     summary: Delete attachments by IDs
         *     tags: [Attachments]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             required:
         *               - ids
         *             properties:
         *               ids:
         *                 type: array
         *                 items:
         *                   type: string
         *     responses:
         *       200:
         *         description: Attachments deleted successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                 message:
         *                   type: string
         *                 deletedIds:
         *                   type: array
         *                   items:
         *                     type: string
         *       400:
         *         description: Invalid or empty IDs array
         *       401:
         *         description: Unauthorized - Invalid or missing Bearer token
         *       404:
         *         description: Attachments not found
         */
        this.router.delete(
            `${this.path}`,
            authMiddleware,
            this.uploadController.deleteImages
        );

        /**
         * @swagger
         * /upload/temporary:
         *   delete:
         *     summary: Delete temporary attachments
         *     tags: [Attachments]
         *     security:
         *       - bearerAuth: []
         *     responses:
         *       200:
         *         description: Temporary attachments deleted successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                 message:
         *                   type: string
         *       401:
         *         description: Unauthorized - Invalid or missing Bearer token
         *       500:
         *         description: Internal server error
         */
        this.router.delete(
            `${this.path}/temporary`,
            authMiddleware,
            this.uploadController.deleteTemporary
        );

        /**
         * @swagger
         * /upload/{id}:
         *   get:
         *     summary: Get attachment by ID
         *     tags: [Attachments]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: The ID of the attachment (MongoDB ObjectId)
         *     responses:
         *       200:
         *         description: Attachment retrieved successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                 message:
         *                   type: string
         *                 data:
         *                   $ref: '#/components/schemas/AttachmentResponse'
         *       401:
         *         description: Unauthorized - Invalid or missing Bearer token
         *       404:
         *         description: Attachment not found
         *       500:
         *         description: Internal server error
         */
        this.router.get(
            `${this.path}/:id`,
            authMiddleware,
            this.uploadController.getById
        );
    }
}

/**
 * @swagger
 * components:
 *   schemas:
 *     AttachmentResponse:
 *       type: object
 *       properties:
 *         _id:
 *           type: string
 *           description: The MongoDB ObjectId of the attachment
 *         type:
 *           type: string
 *           enum: [profileImage, certificate, document]
 *           description: The type of attachment
 *         mimetype:
 *           type: string
 *           description: The MIME type of the file
 *         key:
 *           type: string
 *           description: The storage key for the file
 *         filename:
 *           type: string
 *           description: The original filename
 *         fileSize:
 *           type: number
 *           description: The size of the file in bytes
 *         moduleType:
 *           type: string
 *           enum: [profileImage, certificate, document]
 *           description: The module type of the attachment
 *         url:
 *           type: string
 *           description: The URL to access the file
 */